import "./locationAccountPreferencesAddComponent.scss";
import FormTitleWithNavigationBack
    from "../../../../shared/components/form-title-with-navigation-back/FormTitleWithNavigationBack";
import React, {useCallback, useEffect, useState} from "react";
import * as Yup from "yup";
import {Field, FieldArray, FieldProps, Form, Formik, FormikHelpers} from "formik";
import {CommonService} from "../../../../shared/services";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import FormikRadioButtonGroupComponent
    from "../../../../shared/components/core/formik-radio-button-group/formikRadioButtonGroupComponent";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../../store/reducers";
import FormikInputComponent from "../../../../shared/components/core/formik-input/FormikInputComponent";
import FeedbackComponent from "../../../../shared/components/feedback/feedbackComponent";
import ButtonComponent from "../../../../shared/components/core/button/ButtonComponent";
import {Grid} from "@material-ui/core";
import FormikTimePickerComponent from "../../../../shared/components/core/formik-time-picker/formikTimePickerComponent";
import ChipComponent from "../../../../shared/components/chip/ChipComponent";
import FormikSelectDropdownComponent
    from "../../../../shared/components/core/formik-select-dropdown/FormikSelectDropdownComponent";
import FromikPhoneNumberComponent
    from "../../../../shared/components/core/fromik-phone-number/FromikPhoneNumberComponent";
import {useSearchParams} from "react-router-dom";

interface LocationAccountPreferencesAddComponentProps {
    locationId: number;
    next: Function;
    cancel: Function;
    prev: Function;
    locationDetails: any;
}

const validationSchema = Yup.object({
    preferredReportDelivery: Yup.string().required("Preferred Report Delivery is required"),
    preferredReportDeliveryEmail: Yup.string().when("preferredReportDelivery", {
        is: (val: string) => val === "email",
        then: Yup.string()
            .email("Invalid Email ID")
            .matches(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,"Enter a valid Email ID")
            .required("Email ID is required"),
    }),
    preferredReportDeliveryAutoFax: Yup.string().when("preferredReportDelivery", {
        is: (val: string) => val === "auto-fax",
        then: Yup.string()
            .nullable()
            .required("Fax Number is required")
            .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Fax Number should be 10 digits"),

    }),
    preferredDeliveryMethod: Yup.string().required("Preferred Delivery Method is required"),
    specimenPickUpScheduleType: Yup.string().required("Specimen PickUp ScheduleType is required"),
    day: Yup.mixed(),
    time: Yup.string(),
    specimenPickUpSchedule: Yup.array().when('specimenPickUpScheduleType', {
        is: (specimenPickUpScheduleType: any) => specimenPickUpScheduleType === 'recurring',
        then: Yup.array().of(Yup.object().shape({
            day: Yup.mixed().required("Pickup Day is required"),
            time: Yup.string().required("Pickup Time is required")
                .nullable()
                .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Please enter a valid time: hh:mm'),
        })).min(1, 'atleast 1 specimen pickup schedule  is required')
    })
});


interface FacilityAccountPreferenceDetailsFormSchema {
    preferredReportDelivery: string;
    preferredReportDeliveryValue: string;
    preferredDeliveryMethod: string;
    specimenPickUpScheduleType: string;
    day: string,
    time: string,
    specimenPickUpSchedule: any[];
}

const formInitialValues: FacilityAccountPreferenceDetailsFormSchema = {
    preferredReportDelivery: "",
    preferredReportDeliveryValue: "",
    preferredDeliveryMethod: "",
    specimenPickUpScheduleType: "",
    day: "",
    time: "",
    specimenPickUpSchedule: [],

}

const LocationAccountPreferencesAddComponent = (props: LocationAccountPreferencesAddComponentProps) => {
    const {locationId, next, cancel, prev, locationDetails} = props;
    const [accountPreferenceInitialValues, setAccountPreferenceInitialValues] = useState<FacilityAccountPreferenceDetailsFormSchema>(formInitialValues)
    const {accountPreferenceList} = useSelector((state: IRootReducerState) => state.staticData);
    const [searchParams] = useSearchParams();
    const lastCompletedStep = searchParams.get("lastCompletedStep");

    const onSubmit = useCallback((values: any, {
        setSubmitting,
        setErrors,
    }: FormikHelpers<any>) => {
        const payload = {...values,lastCompletedStep:lastCompletedStep};
        payload.preferredReportDeliveryValue = payload.preferredReportDelivery === "email" ? payload.preferredReportDeliveryEmail : payload.preferredReportDeliveryAutoFax?.replace(/[^0-9]/g, "");
        delete payload.preferredReportDeliveryEmail;
        delete payload.preferredReportDeliveryAutoFax;
        delete payload.day;
        delete payload.time;
        setSubmitting(true);
        CommonService._location.addLocationAccountPreferences(locationId, payload)
            .then((response: IAPIResponseType<any>) => {
                setSubmitting(false);
                next(response.data.accountPreferences);
            })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
            });
    }, [locationId, next,lastCompletedStep]);

    useEffect(() => {
        if (locationDetails?.accountPreferences) {
            const {accountPreferences} = locationDetails;
            if (accountPreferences.preferredReportDelivery === "email") {
                accountPreferences.preferredReportDeliveryEmail = accountPreferences.preferredReportDeliveryValue;
            } else {
                accountPreferences.preferredReportDeliveryAutoFax = accountPreferences.preferredReportDeliveryValue;
            }
            setAccountPreferenceInitialValues({
                ...accountPreferences
            })
        }
    }, [locationDetails]);

    return (
        <div className={'location-account-preferences-add-component screen t-form-screen'}>
            <FormTitleWithNavigationBack
                title={'Account Preferences'}
                handleClick={prev}
                tooltipData={"This information corresponds to the location's preferred report delivery method, as well as a sample delivery method and schedule."}
            />
            <div className="t-form t-form-back-navigation">
                <Formik
                    validationSchema={validationSchema}
                    initialValues={accountPreferenceInitialValues}
                    validateOnChange={true}
                    validateOnBlur={true}
                    validateOnMount={true}
                    enableReinitialize={true}
                    onSubmit={onSubmit}
                >
                    {({isSubmitting, values, isValid, errors, setFieldValue, validateForm}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values, errors]);
                        return (
                            <Form noValidate={true} className={"facility-admin-details-add-holder"}>
                                <div className="t-form-controls-wrapper">
                                    <div className="preferred-delivery-report-section">
                                        <div className="sub-header">Preferred Delivery Report</div>
                                        <>
                                            {
                                                accountPreferenceList?.preferredDeliveryReport?.length ? <>
                                                    <Field name={'preferredReportDelivery'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikRadioButtonGroupComponent
                                                                    options={accountPreferenceList.preferredDeliveryReport}
                                                                    direction={"row"}
                                                                    titleKey={'title'}
                                                                    valueKey={'code'}
                                                                    formikField={field}
                                                                    onUpdate={() => setFieldValue("preferredReportDeliveryValue", "")}
                                                                />
                                                            )}
                                                    </Field>
                                                </> : <>
                                                    No Preferred Report Delivery options available
                                                </>
                                            }
                                        </>

                                        <div>
                                            {
                                                values?.preferredReportDelivery === "auto-fax" && <>
                                                    <Field name={'preferredReportDeliveryAutoFax'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FromikPhoneNumberComponent
                                                                    label={'Fax Number'}
                                                                    placeholder={'(000) 000-0000'}
                                                                    maxLength={14}
                                                                    required={true}
                                                                    formikField={field}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </>
                                            }
                                            {
                                                values?.preferredReportDelivery === "email" && <>
                                                    <Field name={'preferredReportDeliveryEmail'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    label={'Email ID'}
                                                                    placeholder={'Enter Email ID'}
                                                                    required={true}
                                                                    type={"email"}
                                                                    formikField={field}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </>
                                            }
                                            {
                                                values?.preferredReportDelivery === "emr-ems-interface" && <>
                                                    <FeedbackComponent>
                                                        Note: For EMR / EMS Interface, minimun of 250 samples per month
                                                        are required. The AdvanzInnovation Labs will need 3 months to complete the
                                                        interface setup.
                                                    </FeedbackComponent>
                                                </>
                                            }
                                        </div>
                                    </div>

                                    <div className="horizontal-line"/>
                                    <div className="specimen-pickup-deliver-section">
                                        <div className="sub-header">Specimen Pickup Delivery Method</div>
                                        {
                                            accountPreferenceList?.pickupMethod?.length ? <>
                                                <Field name={'preferredDeliveryMethod'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikRadioButtonGroupComponent
                                                                options={accountPreferenceList.pickupMethod}
                                                                direction={"row"}
                                                                titleKey={'title'}
                                                                valueKey={'code'}
                                                                formikField={field}/>
                                                        )}
                                                </Field>
                                            </> : <> No Specimen Pickup Delivery Methods available </>
                                        }
                                    </div>

                                    <div className="horizontal-line"/>
                                    <div className="sub-header">Specimen Pickup Schedule</div>
                                    {
                                        accountPreferenceList?.pickupSchedule?.length ? <>
                                            <Field name={'specimenPickUpScheduleType'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikRadioButtonGroupComponent
                                                            options={accountPreferenceList.pickupSchedule}
                                                            direction={"row"}
                                                            titleKey={'title'}
                                                            valueKey={'code'}
                                                            formikField={field}/>
                                                    )}
                                            </Field>
                                        </> : <>No Specimen Pickup Delivery Schedule available</>
                                    }
                                    {
                                        values.specimenPickUpScheduleType === 'call-lab' &&
                                        <FeedbackComponent className={'width-50 mrg-top-10'}>
                                            Call Lab whenever the specimen pickup is ready.
                                        </FeedbackComponent>
                                    }

                                    {values.specimenPickUpScheduleType === 'recurring' && <FieldArray
                                        name={"specimenPickUpSchedule"}
                                        render={(arrayHelpers) => (
                                            <div className="pdd-top-10">
                                                    <Grid container spacing={2} xs={12}>
                                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                                            <Field name={'day'}>
                                                                {
                                                                    (field: FieldProps) => (
                                                                        <FormikSelectDropdownComponent
                                                                            label={'Pickup Day'}
                                                                            placeholder={'Select Pickup Day'}
                                                                            required={true}
                                                                            options={CommonService._staticData.weekDays}
                                                                            formikField={field}
                                                                            valueKey={'code'}
                                                                            getOptionLabel={(option => (option.title) || '')}
                                                                        />
                                                                    )
                                                                }
                                                            </Field>
                                                        </Grid>
                                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                                            <Field name={'time'}>
                                                                {
                                                                    (field: FieldProps) => (
                                                                        <FormikTimePickerComponent
                                                                            label={'Pickup Time'}
                                                                            placeholder={'Select Pickup Time'}
                                                                            required={true}
                                                                            fullWidth={true}
                                                                            formikField={field}
                                                                        />
                                                                    )
                                                                }
                                                            </Field>
                                                        </Grid>
                                                    </Grid>
                                                <div className={"d-flex justify-content-flex-end"}>
                                                    <ButtonComponent
                                                        type="button"
                                                        color={"secondary"}
                                                        variant={"outlined"}
                                                        fullWidth={false}
                                                        disabled={!(values.day !== "" && values.time !== "")}
                                                        handleClick={() => {
                                                            arrayHelpers.push({
                                                                "day": values.day,
                                                                "time": values.time
                                                            })
                                                            setFieldValue("day", "");
                                                            setFieldValue("time", "");
                                                        }}
                                                    >
                                                        Add
                                                    </ButtonComponent>
                                                </div>
                                                {
                                                    values?.specimenPickUpSchedule.length === 0 &&
                                                    <FeedbackComponent className={'width-50 margin-top-20'}>
                                                        No Pickup Day and Time have been added.
                                                    </FeedbackComponent>
                                                }
                                                <div className={'display-flex-with-column-gap mrg-top-20'}>
                                                    {values?.specimenPickUpSchedule && values.specimenPickUpSchedule.map((dayAndTime: any, index: any) => {
                                                            return (
                                                                <div key={index}>
                                                                    <ChipComponent
                                                                        label={`${CommonService.capitalizeFirstLetter(dayAndTime.day)} | ${dayAndTime.time}`}
                                                                        onDelete={arrayHelpers.handleRemove(index)}
                                                                    />
                                                                </div>
                                                            )
                                                        }
                                                    )
                                                    }
                                                </div>

                                            </div>
                                        )
                                        }
                                    />}

                                </div>
                                <div className="form-action-section">
                                    <ButtonComponent
                                        type="button"
                                        color={"primary"}
                                        variant={"outlined"}
                                        fullWidth={true}
                                        disabled={isSubmitting}
                                        handleClick={cancel}
                                    >
                                        Close
                                    </ButtonComponent>
                                    <ButtonComponent
                                        type="submit"
                                        color={"primary"}
                                        variant={"contained"}
                                        fullWidth={true}
                                        disabled={!isValid}
                                        isLoading={isSubmitting}
                                    >
                                        Next
                                    </ButtonComponent>
                                </div>
                            </Form>
                        )
                    }
                    }
                </Formik>
            </div>
        </div>
    )
        ;

};

export default LocationAccountPreferencesAddComponent;
